/**
 * useTrace — React hook for unified tracing.
 *
 * Wraps TraceClient for component-level instrumentation.
 * Must be called unconditionally (React Rules of Hooks).
 * All returned functions are no-ops when tracing is disabled.
 *
 * Usage:
 *   const trace = useTrace('comp-datacard-depth');
 *   const threadId = await trace.startTrace('data_source', 'Chart data fetch');
 *   const spanId = await trace.startSpan(threadId, 'ui_data_request', 'Fetch data');
 *   const headers = trace.getTraceHeaders(threadId, spanId);
 *   await trace.endSpan(spanId, 'ok', { detail: { rowCount: 42 } });
 */

import { useCallback, useMemo, useRef } from 'react';
import { traceClient } from '@/services/tracing/TraceClient';
import type { ThreadInitiator, SpanKind } from '@/services/api/agentApi';

export interface UseTraceReturn {
  /** Whether tracing is currently enabled */
  isEnabled: boolean;
  /** Create a new trace thread. Returns threadId or null. */
  startTrace: (initiator: ThreadInitiator, title?: string) => Promise<string | null>;
  /** Start a span within a thread. Returns spanId or null. */
  startSpan: (
    threadId: string | null | undefined,
    kind: SpanKind,
    summary: string,
    opts?: { parentSpanId?: string; detail?: Record<string, unknown>; traceLevel?: number }
  ) => Promise<string | null>;
  /** End a span. No-op if spanId is null. */
  endSpan: (
    spanId: string | null | undefined,
    status: 'ok' | 'error',
    opts?: { summary?: string; detail?: Record<string, unknown>; errorMessage?: string }
  ) => Promise<void>;
  /** Build trace headers for outgoing HTTP requests. */
  getTraceHeaders: (threadId: string | null | undefined, parentSpanId?: string | null) => Record<string, string>;
}

/**
 * Hook for unified tracing. Called unconditionally at top of hook/component.
 * All async methods are fire-and-forget — they never throw or block rendering.
 */
export function useTrace(componentId: string): UseTraceReturn {
  const componentIdRef = useRef(componentId);
  componentIdRef.current = componentId;

  const startTrace = useCallback(async (
    initiator: ThreadInitiator,
    title?: string
  ): Promise<string | null> => {
    const result = await traceClient.createThread(
      initiator,
      title || `${initiator}:${componentIdRef.current}`,
      componentIdRef.current
    );
    return result?.threadId ?? null;
  }, []);

  const startSpan = useCallback(async (
    threadId: string | null | undefined,
    kind: SpanKind,
    summary: string,
    opts?: { parentSpanId?: string; detail?: Record<string, unknown>; traceLevel?: number }
  ): Promise<string | null> => {
    return traceClient.startSpan(threadId, kind, summary, {
      ...opts,
      agent: componentIdRef.current,
    });
  }, []);

  const endSpan = useCallback(async (
    spanId: string | null | undefined,
    status: 'ok' | 'error',
    opts?: { summary?: string; detail?: Record<string, unknown>; errorMessage?: string }
  ): Promise<void> => {
    await traceClient.endSpan(spanId, status, opts);
  }, []);

  const getTraceHeaders = useCallback((
    threadId: string | null | undefined,
    parentSpanId?: string | null
  ): Record<string, string> => {
    return traceClient.getTraceHeaders(threadId, parentSpanId);
  }, []);

  const isEnabled = traceClient.isEnabled();

  return useMemo(() => ({
    isEnabled,
    startTrace,
    startSpan,
    endSpan,
    getTraceHeaders,
  }), [endSpan, getTraceHeaders, isEnabled, startSpan, startTrace]);
}
